import authService from './authService';
import {
  ApplySuggestionItem,
  ApplySuggestionsResponse,
  PaginationInfo,
  ProductHistoryResponse,
  PurchasePlanHorizonResponse,
  PurchasePlanningResponse,
  StockAnalyticsParameters,
  StockAnalyticsSummary,
  StockMetricsFilter,
  StockMetricsRow,
} from '../types/stockAnalytics';

// Endpoints da Análise de Estoque (ERPApi). Todas as respostas usam o
// envelope { success, data } — este service já devolve o `data` desembrulhado.

class StockAnalyticsService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getMetrics(filter: StockMetricsFilter): Promise<{ rows: StockMetricsRow[]; pagination: PaginationInfo | null }> {
    const params = new URLSearchParams();
    params.set('page', String(filter.page ?? 1));
    params.set('limit', String(filter.limit ?? 50));
    if (filter.search) params.set('search', filter.search);
    if (filter.abc_class) params.set('abc_class', filter.abc_class);
    if (filter.xyz_class) params.set('xyz_class', filter.xyz_class);
    if (filter.slow_moving_class !== undefined && filter.slow_moving_class !== null) {
      params.set('slow_moving_class', String(filter.slow_moving_class));
    }
    if (filter.provider_id) params.set('provider_id', String(filter.provider_id));
    if (filter.excess_only) params.set('excess_only', 'true');
    if (filter.stockout_risk_only) params.set('stockout_risk_only', 'true');
    if (filter.sort_by) params.set('sort_by', filter.sort_by);
    if (filter.sort_dir) params.set('sort_dir', filter.sort_dir);

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/stock-analytics?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar análise de estoque');
    const body = await response.json();
    return { rows: body.data ?? [], pagination: body.pagination ?? null };
  }

  async getSummary(): Promise<StockAnalyticsSummary> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/reports/stock-analytics/summary');
    if (!response.ok) throw new Error('Erro ao carregar resumo da análise de estoque');
    const body = await response.json();
    return body.data;
  }

  async getProductHistory(productId: number): Promise<ProductHistoryResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/stock-analytics/products/${productId}/history`);
    if (!response.ok) throw new Error('Erro ao carregar histórico do produto');
    const body = await response.json();
    return body.data;
  }

  async getPurchasePlanning(providerId?: number | null, onlyWithSuggestion = true): Promise<PurchasePlanningResponse> {
    const params = new URLSearchParams();
    if (providerId) params.set('provider_id', String(providerId));
    params.set('only_with_suggestion', onlyWithSuggestion ? 'true' : 'false');

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/purchase-planning?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar planejamento de compras');
    const body = await response.json();
    return body.data;
  }

  /** Prévia do PC cobrindo os próximos `months` meses (1–12) de um fornecedor. */
  async getPurchasePlanHorizon(providerId: number, months: number): Promise<PurchasePlanHorizonResponse> {
    const params = new URLSearchParams({ provider_id: String(providerId), months: String(months) });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/purchase-planning/horizon?${params}`);
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.message ?? 'Erro ao calcular o pedido de compra por horizonte');
    return body.data;
  }

  async applySuggestions(items: ApplySuggestionItem[]): Promise<ApplySuggestionsResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/stock-analytics/apply-suggestions', { items });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.message ?? 'Erro ao aplicar sugestões de estoque mínimo/máximo');
    }
    return body.data;
  }

  async getParameters(): Promise<StockAnalyticsParameters> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/stock-analytics/parameters');
    if (!response.ok) throw new Error('Erro ao carregar parâmetros de cálculo');
    const body = await response.json();
    return body.data;
  }

  async updateParameters(params: StockAnalyticsParameters): Promise<StockAnalyticsParameters> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put('/stock-analytics/parameters', params);
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.message ?? 'Erro ao salvar parâmetros de cálculo');
    }
    return body.data;
  }
}

export const stockAnalyticsService = new StockAnalyticsService();
export default stockAnalyticsService;
